/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable jsx-a11y/anchor-has-content */

import React, { useState, useEffect, useRef } from "react";
import { graphql, useStaticQuery } from "gatsby";
import tw, { css } from "twin.macro";

import { useMediaQuery } from "~hooks";

import Button from "~components/Button.jsx";
import * as Icon from "~components/svg/Icons.jsx";
import * as A from "~components/styles/Animations.jsx";
import Grid from "~components/styles/Grid.jsx";
import * as T from "~components/styles/Typography.jsx";

import { fancyError, fancyLog, validateEmail } from "~utils/helpers";

const PopUpWorkplaceSection = ({ overlayActive, setOverlayActive }) => {
  const {
    sanityFormWorkplaces: {
      textColor,
      backgroundColor,
      formTextColor,
      formBackgroundColor,
      header,
      subheader,
      resources
    }
  } = useStaticQuery(graphql`
    query {
      sanityFormWorkplaces {
        textColor {
          hex
        }
        backgroundColor {
          hex
        }
        formTextColor {
          hex
        }
        formBackgroundColor {
          hex
        }
        header
        subheader
        resources {
          asset {
            url
          }
        }
      }
    }
  `);

  const { isDesktop } = useMediaQuery();

  return (
    <div
      css={[
        css`
          transition: 0.5s ${A.EASING_CUBIC} opacity,
            0.5s ${A.EASING_CUBIC} transform;

          opacity: ${overlayActive ? `1` : `0`};
          pointer-events: ${overlayActive ? `auto` : `none`};
          transform: scale(${overlayActive ? `1` : `1.05`});
        `,
        tw`w-screen h-screen fixed top-0 right-0 bottom-0 left-0 z-40 flex items-center justify-center`
      ]}
    >
      <div
        role="presentation"
        css={[
          tw`w-screen h-screen absolute top-0 right-0 bottom-0 left-0 z-10 bg-black opacity-50 cursor-pointer`
        ]}
        onClick={() => setOverlayActive(false)}
      />

      <div
        css={[
          tw`w-full max-height[90vh] relative z-20 block overflow-y-scroll`
        ]}
      >
        <Grid>
          <div
            css={[
              tw`col-span-22 md:col-span-20 lg:col-span-18 col-start-1 md:col-start-3 lg:col-start-4 relative md:flex`
            ]}
          >
            <div css={[tw`w-12 h-12 absolute top-0 right-0 z-10`]}>
              <button
                css={[tw`p-4`]}
                type="button"
                onClick={() => {
                  setOverlayActive(false);
                }}
              >
                <Icon.Cross
                  _css={[tw`w-full h-full relative block`]}
                  color={isDesktop ? `orange-dark` : `white`}
                />
              </button>
            </div>

            <div
              css={[
                tw`w-full md:w-1/2 relative block py-12 px-4 md:p-12`,
                css`
                  color: ${textColor?.hex || `white`};
                  background-color: ${backgroundColor?.hex || `black`};
                `
              ]}
            >
              <T.Head font="3">{header}</T.Head>
              <T.Head _css={[tw`mt-8`]} font="5">
                {subheader}
              </T.Head>
            </div>

            <FormResources
              data={{
                textColor: formTextColor,
                backgroundColor: formBackgroundColor,
                file: resources
              }}
            />
          </div>
        </Grid>
      </div>
    </div>
  );
};

export default PopUpWorkplaceSection;

//

const FormResources = ({ data }) => {
  const { textColor, backgroundColor, file } = data;

  const submitRef = useRef();
  const downloadRef = useRef();

  const [formData, setFormData] = useState({
    "first-name": ``,
    "last-name": ``,
    email: ``,
    company: ``
  });
  const [interacted, setInteracted] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [valid, setValid] = useState(false);

  const inputCSS = `
      background: transparent;
  
      ::-webkit-input-placeholder {
        color: ${textColor?.hex || `black`};
        opacity: 0.5;
      }
      ::-moz-placeholder {
        color: ${textColor?.hex || `black`};
        opacity: 0.5;
      }
      :-ms-input-placeholder {
        color: ${textColor?.hex || `black`};
        opacity: 0.5;
      }
      :-moz-placeholder {
        color: ${textColor?.hex || `black`};
        opacity: 0.5;
      }
    `;

  useEffect(() => {
    if (!interacted) {
      return;
    }

    if (
      !formData[`first-name`] ||
      formData[`first-name`] === `` ||
      !formData[`last-name`] ||
      formData[`last-name`] === `` ||
      !formData.email ||
      !validateEmail(formData.email)
    ) {
      setValid(false);
    } else {
      setValid(true);
    }
  }, [formData]);

  const submitProxy = () => {
    if (!submitRef?.current) {
      return;
    }

    submitRef.current.click();
  };

  const onChange = (e) => {
    setInteracted(true);
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (
      typeof window !== `undefined` &&
      window.location.href.includes(`localhost:8000`)
    ) {
      setSubmitting(true);

      setTimeout(() => {
        setSubmitting(false);
        setSubmitted(true);
        setNewsletter(false);
        setFormData({
          "first-name": ``,
          "last-name": ``,
          email: ``,
          company: ``
        });

        if (downloadRef?.current) {
          downloadRef.current.click();
        }
      }, 3000);

      return false;
    }

    if (!valid || submitting || submitted) {
      return false;
    }

    setSubmitting(true);

    if (formData?.email && validateEmail(formData.email)) {
      const mailchimpData = {
        email: formData.email,
        newsletter,
        workplace: true
      };

      fetch(`api/mailchimp`, {
        body: JSON.stringify(mailchimpData),
        headers: new Headers({
          "Content-Type": `application/json`
        }),
        method: `PUT`
      })
        .then(() => {
          setNewsletter(false);
          fancyLog(`Mailchimp complete`);
        })
        .catch((error) => {
          fancyError(error);
        });
    }

    if (downloadRef?.current) {
      downloadRef.current.click();
    }

    return false;
  };

  return (
    <div
      css={[
        tw`w-full md:w-1/2 relative block py-12 px-4 md:p-12`,
        css`
          color: ${textColor?.hex || `black`};
          background-color: ${backgroundColor?.hex || `white`};
        `
      ]}
    >
      <form css={[tw`w-full relative block`]} onSubmit={onSubmit}>
        <input
          ref={submitRef}
          tw="w-0 h-0 absolute opacity-0 pointer-events-none"
          type="submit"
          hidden
        />

        <a
          ref={downloadRef}
          tw="w-0 h-0 absolute opacity-0 pointer-events-none"
          aria-hidden="true"
          href={file?.asset?.url}
          download
          target="_blank"
        />

        <T.Body _css={[tw`mb-6`]} font="3">
          Your Details:
        </T.Body>

        <div
          css={[tw`w-full relative md:flex md:items-center md:justify-between`]}
        >
          <label
            htmlFor="first-name"
            css={[
              css`
                width: calc(50% - 0.5rem);

                @media screen and (max-width: 1024px) {
                  width: 100%;
                }
              `,
              tw`relative block`
            ]}
          >
            <input
              name="first-name"
              css={[
                css`
                  ${inputCSS}
                `,
                tw`w-full relative block mb-6 p-1 border-orange-dark border-b font-body text-b1 md:text-b1-md`
              ]}
              placeholder="Your First Name*"
              required
              type="text"
              value={formData[`first-name`]}
              onChange={onChange}
            />
          </label>

          <label
            htmlFor="last-name"
            css={[
              css`
                width: calc(50% - 0.5rem);

                @media screen and (max-width: 1024px) {
                  width: 100%;
                }
              `,
              tw`relative block`
            ]}
          >
            <input
              name="last-name"
              css={[
                css`
                  ${inputCSS}
                `,
                tw`w-full relative block mb-6 p-1 border-orange-dark border-b font-body text-b1 md:text-b1-md`
              ]}
              placeholder="Your Last Name*"
              required
              type="text"
              value={formData[`last-name`]}
              onChange={onChange}
            />
          </label>
        </div>

        <label htmlFor="email" css={[css``, tw`w-full relative block`]}>
          <input
            name="email"
            css={[
              css`
                ${inputCSS}
              `,
              tw`w-full relative block mb-6 p-1 border-orange-dark border-b font-body text-b1 md:text-b1-md`
            ]}
            placeholder="Your Email*"
            required
            type="email"
            value={formData.email}
            onChange={onChange}
          />
        </label>

        <label htmlFor="company" css={[tw`w-full relative block`]}>
          <input
            name="company"
            css={[
              css`
                ${inputCSS}
              `,
              tw`w-full relative block mb-6 p-1 border-orange-dark border-b font-body text-b1 md:text-b1-md`
            ]}
            placeholder="Company"
            type="text"
            value={formData.company}
            onChange={onChange}
          />
        </label>

        <div
          css={[
            tw`relative flex items-start md:items-center mt-16 md:mt-24 mb-6 md:mb-2`
          ]}
        >
          <input
            css={[tw`w-4 h-4 relative mt-1 md:mt-0 mr-3 block border`]}
            type="checkbox"
            checked={newsletter}
            onChange={(e) => {
              setNewsletter(e.currentTarget.checked);
            }}
          />

          <T.Body font="2">
            I agree to receiving a newsletter from Eat Up
          </T.Body>
        </div>

        <Button
          _css={[
            tw`w-full md:w-auto`,
            submitting && tw`bg-transparent! text-orange-dark! opacity-50`
          ]}
          color="orange-dark"
          text="Download"
          onClick={submitProxy}
          disabled={submitting}
        />
      </form>
    </div>
  );
};
